import { NumberInput, type NumberInputProps } from '@shared/components';
import { useMemo } from 'react';
import { useController, type FieldValues, type UseControllerProps } from 'react-hook-form';

import { getDifficultyCriteria } from '../../utils';

import { DEFAULT_CRITERIA } from './constants';
import { DifficultyText } from './difficulty-text';
import type { PValueCriteria } from './types';

export interface PValueFieldProps<TFieldValues extends FieldValues>
  extends Omit<NumberInputProps, 'name' | 'disabled'> {
  criteria?: PValueCriteria[];
  controllerProps: UseControllerProps<TFieldValues>;
}

export default function PValueField<TFieldValues extends FieldValues = FieldValues>(
  props: Readonly<PValueFieldProps<TFieldValues>>,
) {
  const { criteria = DEFAULT_CRITERIA, controllerProps, ...restProps } = props;

  const {
    field: { ref, ...field },
    fieldState,
  } = useController(controllerProps);

  const errorMessages = useMemo(() => {
    return fieldState.error?.message;
  }, [fieldState.error]);

  // NOTE: Since we had view and form state, if criteria in change someday
  // Don't forget to change at InformationView container too (They got a value qcontent-provider)
  const currentCriteria = useMemo(() => {
    return getDifficultyCriteria(criteria, field.value);
  }, [field.value]);

  return (
    <NumberInput
      {...field}
      slotProps={{
        input: {
          endAdornment: currentCriteria ? <DifficultyText criteria={currentCriteria} /> : undefined,
          autoComplete: 'off',
        },
      }}
      error={Boolean(errorMessages)}
      helperText={errorMessages}
      value={field.disabled && !field.value ? '-' : field.value}
      maxDecimalPlaces={2}
      inputRef={ref}
      {...restProps}
    />
  );
}
